import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../state/authStore';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Activity } from 'lucide-react';
import { track, EVT } from '../../lib/analytics';
import SupabaseAuthOptions from '../../components/auth/SupabaseAuthOptions';

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

export default function SignupPage() {
  const navigate = useNavigate();
  const signup = useAuthStore((s) => s.signup);
  const isLoading = useAuthStore((s) => s.isLoading);
  const [userType, setUserType] = useState('retail');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pan, setPan] = useState('');
  const [orgName, setOrgName] = useState('');
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState('');
  const [confirmSent, setConfirmSent] = useState(false);

  useEffect(() => { track(EVT.SIGNUP_VIEW); }, []);

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    track(EVT.SIGNUP_SUBMITTED, { user_type: userType });

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (!consent) {
      setError('Please agree to the Terms and Privacy Policy to continue.');
      return;
    }
    if (pan && !PAN_REGEX.test(pan.toUpperCase())) {
      setError('PAN must be 10 characters in the format AAAAA9999A.');
      return;
    }
    if (userType === 'institutional' && !orgName.trim()) {
      return setError('Company name is required.');
    }

    const result = await signup(name, email, password, userType, 'Admin', {
      pan: pan ? pan.toUpperCase() : undefined,
      org: userType === 'institutional' ? { name: orgName.trim() } : undefined,
    });

    if (!result.success) {
      setError(result.error || 'Signup failed. Please try again.');
      return;
    }
    if (result.confirmEmail) {
      setConfirmSent(true);
      return;
    }
    if (userType === 'institutional') {
      navigate('/public/org-settings?onboarding=1', { replace: true });
    } else {
      navigate('/public/home', { replace: true });
    }
  };

  if (confirmSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950 px-4">
        <Card className="w-full max-w-md border-gray-800 bg-gray-900">
          <CardContent className="p-8 text-center space-y-4">
            <div className="p-3 rounded-xl bg-emerald-600/20 inline-block">
              <Activity className="w-8 h-8 text-emerald-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-100">Check your email</h2>
            <p className="text-sm text-gray-400">
              We've sent a confirmation link to <span className="text-gray-200 font-medium">{email}</span>.
              Click it to activate your account.
            </p>
            <Button variant="outline" onClick={() => navigate('/login')} className="border-gray-700 text-gray-300 hover:bg-gray-800">
              Back to Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-950 px-4 py-8">
      <Card className="w-full max-w-lg border-gray-800 bg-gray-900">
        <CardHeader className="text-center space-y-2">
          <div className="flex justify-center mb-2">
            <div className="p-3 rounded-xl bg-emerald-600/20">
              <Activity className="w-8 h-8 text-emerald-500" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-gray-100">Create Account</CardTitle>
          <CardDescription className="text-gray-400">
            {userType === 'institutional'
              ? 'For advisors, research desks, PMS, family offices and other institutions.'
              : 'KubAIr Research for investors. Free to start.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSignup} className="space-y-4">
            {error && (
              <Alert className="border-red-800 bg-red-950">
                <AlertDescription className="text-red-300">
                  {error}
                  {error.includes('already registered') && (
                    <button
                      type="button"
                      onClick={() => navigate('/login')}
                      className="ml-2 text-emerald-400 hover:underline font-medium"
                    >
                      Sign in →
                    </button>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {/* Account type toggle */}
            <div className="space-y-2">
              <Label className="text-gray-300">I am signing up as…</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setUserType('retail')}
                  className={`p-3 rounded-lg border text-sm font-medium transition-colors ${
                    userType === 'retail'
                      ? 'border-emerald-500 bg-emerald-950 text-emerald-400'
                      : 'border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-600'
                  }`}
                >
                  An investor (Retail)
                </button>
                <button
                  type="button"
                  onClick={() => setUserType('institutional')}
                  className={`p-3 rounded-lg border text-sm font-medium transition-colors ${
                    userType === 'institutional'
                      ? 'border-emerald-500 bg-emerald-950 text-emerald-400'
                      : 'border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-600'
                  }`}
                >
                  An institution
                </button>
              </div>
            </div>

            {/* Common identity */}
            <div className="space-y-2">
              <Label className="text-gray-300">Full Name</Label>
              <Input type="text" placeholder="Ravi Kumar" value={name} onChange={(e) => setName(e.target.value)} required
                className="bg-gray-800 border-gray-700 text-gray-100" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Work email</Label>
              <Input type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="bg-gray-800 border-gray-700 text-gray-100" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Password</Label>
              <Input type="password" placeholder="Minimum 8 characters" value={password} onChange={(e) => setPassword(e.target.value)} required
                className="bg-gray-800 border-gray-700 text-gray-100" />
            </div>

            {/* Retail-only: optional PAN */}
            {userType === 'retail' && (
              <div className="space-y-2">
                <Label className="text-gray-300">PAN (optional)</Label>
                <Input type="text" placeholder="AAAAA9999A" value={pan} onChange={(e) => setPan(e.target.value.toUpperCase())} maxLength={10}
                  className="bg-gray-800 border-gray-700 text-gray-100 uppercase tracking-wider" />
                <p className="text-xs text-gray-500">
                  Required later to connect a broker. Stored encrypted, never shared.
                </p>
              </div>
            )}

            {/* Institutional-only: just the company name for now. SEBI
                registration, compliance officer, registered address, etc.
                can be collected later in Org Settings once the account is
                active — keeping signup to a single field so firms can get
                in fast. */}
            {userType === 'institutional' && (
              <div className="space-y-2">
                <Label className="text-gray-300">Company name</Label>
                <Input
                  type="text"
                  placeholder="Acme Wealth Advisors Pvt Ltd"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  required
                  className="bg-gray-800 border-gray-700 text-gray-100"
                />
                <p className="text-xs text-gray-500">
                  You can add SEBI registration, compliance officer and other firm details later in Settings.
                </p>
              </div>
            )}

            {/* Consent — required for both */}
            <label className="flex gap-2 items-start text-xs text-gray-400 cursor-pointer">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />
              <span>
                I agree to the{' '}
                <a href="/terms" target="_blank" rel="noreferrer" className="text-emerald-400 hover:underline">Terms of Service</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" rel="noreferrer" className="text-emerald-400 hover:underline">Privacy Policy</a>.
                I consent to KubAIr processing my data per the Digital Personal Data Protection Act, 2023.
              </span>
            </label>

            <Button type="submit" disabled={isLoading} className="w-full bg-emerald-600 hover:bg-emerald-700">
              {isLoading ? 'Creating account…' : (userType === 'institutional' ? 'Create firm account' : 'Create account')}
            </Button>
            <p className="text-xs text-gray-500 text-center">Password must be at least 8 characters.</p>
          </form>

          {/* Retail-only: third-party auth. Institutional signup needs the
              company name, which we can't capture through a one-click Google
              flow, so keep it email-only for institutional. */}
          {userType === 'retail' && (
            <div className="mt-5">
              <SupabaseAuthOptions mode="signup" />
            </div>
          )}

          <div className="text-center text-sm text-gray-400 mt-5">
            Already have an account?{' '}
            <button type="button" onClick={() => navigate('/login')} className="text-emerald-500 hover:underline font-medium">
              Sign In
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
